import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import Link from 'next/link';

import { JobPositionFeatures } from '@pflegia/types/common/job-position/features';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { ReactComponent as Briefcase } from '@pflegia/ui/icons/briefcase.svg';
import { ReactComponent as MapPin } from '@pflegia/ui/icons/map-pin.svg';
import { getButtonClassName } from '@pflegia/ui/shared/button';
import { FeaturesBadges } from '@pflegia/ui/shared/job-position/FeaturesBadges';
import LinkWithLoader from '@pflegia/ui/shared/link-with-loader/LinkWithLoader.component';
import { shouldImageBeUnoptimized } from '@pflegia/ui/shared/utils/image-unoptimized';
import { formatSalaryRange } from '@pflegia/util/common/format-salary-range';
import { getJobPositionPathname } from '@pflegia/util/functions/job-position/helpers';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import OffersDialogueButton from '@shared/components/dialogue/OffersDialogueButton';

export interface CarouselJobCardJob {
  id: string;
  title: string;
  headline?: string | null;
  slug?: string | null;
  salaryLow?: number | null;
  salaryHigh?: number | null;
  showSalary?: boolean | null;
  geoLocation?: { city?: string | null; postalCode?: string | null } | null;
  employer: { name: string; logoKey?: string | null; showSalary?: boolean | null };
}

interface CarouselJobCardProps {
  job: CarouselJobCardJob;
  featuresBadges?: JobPositionFeatures & { loading?: boolean };
  openDialog: (connectionInfo: ConnectionInfo) => void;
}

const formatPostalCity = (postalCode?: string | null, city?: string | null): string | null => {
  if (postalCode && city) return `${postalCode} ${city}`;
  return city ?? null;
};

const CarouselJobCard: React.FC<CarouselJobCardProps> = ({ job, featuresBadges, openDialog }) => {
  const { t } = useTranslation('jobDetails');
  const employerLogo = getPublicImageUrl(job.employer.logoKey) || '';
  const salary = job.showSalary && job.employer.showSalary ? formatSalaryRange(job.salaryLow, job.salaryHigh) : null;
  const location = formatPostalCity(job.geoLocation?.postalCode, job.geoLocation?.city);
  const positionLink = getJobPositionPathname(
    { geoLocation: job.geoLocation, id: job.id, title: job.title, slug: job.slug },
    { basePath: '/job-details', slimmed: true },
  );

  return (
    <div className='flex h-full w-full flex-col justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-3 shadow-md'>
      <div className='flex flex-col gap-2'>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex min-w-0 flex-1 flex-col gap-2'>
            {featuresBadges ? <FeaturesBadges featuresBadges={featuresBadges} /> : null}
          </div>
          {employerLogo ? (
            <div className='bg-img relative h-10 w-10 shrink-0 rounded-md drop-shadow-xs md:h-12 md:w-12'>
              <Image
                alt='Employer Logo'
                className='rounded-md object-contain'
                fill
                quality={75}
                sizes='48px'
                src={employerLogo}
                unoptimized={shouldImageBeUnoptimized(employerLogo)}
              />
            </div>
          ) : null}
        </div>
        <Link href={positionLink} className='hover:underline'>
          <h4 className='text-default line-clamp-3 text-sm font-bold md:text-base'>{job.headline ?? job.title}</h4>
        </Link>
        <p className='text-secondary text-xs md:text-sm'>{job.employer.name}</p>
      </div>
      <div className='flex flex-col gap-4 pb-4'>
        <div className='text-default flex flex-col gap-2 text-xs md:text-sm'>
          {location ? (
            <div className='flex items-center gap-2'>
              <MapPin className='text-funnel-blue shrink-0' width={20} height={20} />
              <span className='truncate'>{location}</span>
            </div>
          ) : null}
          {salary ? (
            <div className='flex items-center gap-2'>
              <Briefcase className='text-funnel-blue shrink-0' width={20} height={20} />
              <span className='truncate'>{salary}</span>
            </div>
          ) : null}
        </div>

        <div className='grid w-full grid-cols-2 gap-2 sm:w-auto'>
          <LinkWithLoader
            href={positionLink}
            className={getButtonClassName('secondary', 'min-w-0 flex-1 justify-center text-xs whitespace-nowrap lg:text-sm')}>
            {t('abTestNewComponents.otherJobsCarousel.learnMore')}
          </LinkWithLoader>
          <OffersDialogueButton
            className='min-w-0 flex-1 justify-center text-xs whitespace-nowrap lg:text-sm'
            jobPositionId={job.id}
            jobTitle={job.title}
            slug={job.slug}
            employerName={job.employer.name}
            label={t('abTestNewComponents.otherJobsCarousel.apply')}
            openDialog={openDialog}
          />
        </div>
      </div>
    </div>
  );
};

export default CarouselJobCard;
